import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router'; import { GetAllCompany } from '@edtech-ui/core/models/company-management/get-all-companies-list';
import { ToastrService } from 'ngx-toastr';
import { debounceTime, distinctUntilChanged, finalize, Subject, Subscription, tap } from 'rxjs';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { AdminStudentPlatformHeaderComponent } from '@edtech-ui/admin-student-platform/admin-student-platform-header/admin-student-platform-header.component';
import { RecruitmentJobService } from '@edtech-ui/core/services/recruitment-management/recruitment-job.service';
import { SuperAdminAllJobs } from '@edtech-ui/core/models/company-management/admin-all-jobs';
import { MatTooltipModule } from '@angular/material/tooltip';


@Component({
	selector: 'app-superadmin-all-jobs',
	standalone: true,
	imports: [CommonModule, ReactiveFormsModule, FormsModule, MatProgressSpinnerModule,
		AdminStudentPlatformHeaderComponent ,MatTooltipModule
	],
	templateUrl: './superadmin-all-jobs.component.html',
	styleUrl: './superadmin-all-jobs.component.scss'
})
export class SuperadminAllJobsComponent implements OnInit, OnDestroy {

	searchTerm = '';
	selectedStatus = '';
	selectedWorkMode = '';
	selectedIndustry = 'All Industries';
	isLoading = false;
	showOnboardModal = false;
	showDetailedModal = false;
	showActionMenuId: string | null = null;
	industries: string[] = [];
	jobData: SuperAdminAllJobs[] = [];
	allJobsData: any[] = [];
	selectedCompany: GetAllCompany | null = null;
	showApproveModal = false;
	showRejectModal = false;
	isApprovalLoading: boolean = false;
	rejectionReason = '';
	total = 0;
	page = 1;
	limit = 10;
	totalPages = 1;
	status = '';
	sortField = '';
	sortDirection: 'asc' | 'desc' = 'asc';
	viewType: 'card' | 'table' = 'table';
	subscription = new Subscription();
	searchSubscription = new Subscription();
	showingFrom = 0;
	showingTo = 0;
	searchSubject = new Subject<string>();
	selectedCreatedDate = '';
	selectedDeadlineDate = '';
	selectedJobRole = '';
	jobRoles: string[] = [];
	selectedCompanyFilter = '';
	companies: string[] = [];


	constructor(
		private toastr: ToastrService,
		private router: Router,
		private jobService: RecruitmentJobService,
		private route: ActivatedRoute
	) { }

	ngOnInit(): void {
		const initialPage = Number(this.route.snapshot.queryParams['page']);
		if (initialPage && !isNaN(initialPage) && initialPage > 0) {
			this.page = initialPage;
		}

		this.searchSubscription.add(
			this.searchSubject.pipe(debounceTime(500), distinctUntilChanged()).subscribe(() => {
				this.page = 1;
				this.getAllJobs();
			}));
		this.loadFilterSuggestions();
		this.getAllJobs();

		this.subscription.add(
			this.route.queryParams.subscribe(params => {
				const queryPage = Number(params['page']);
				if (queryPage && !isNaN(queryPage) && queryPage > 0 && queryPage !== this.page) {
					this.page = queryPage;
					this.getAllJobs();
				}
			})
		);
	}

	loadFilterSuggestions(): void {
		this.subscription.add(
			this.jobService.getAllJobs(1, 100, 'active').subscribe({
				next: (response) => {
					if (response.Success && response.Data?.jobs) {
						this.jobRoles = Array.from(
							new Set(response.Data.jobs.map(job => job.job_title).filter(Boolean))
						).sort() as string[];
						this.companies = Array.from(
							new Set(response.Data.jobs.map(job => job.company_name).filter(Boolean))
						).sort() as string[];
					}
				}
			})
		);
	}

	onStatusChange(): void {

		if (this.selectedStatus === 'All Status') {
			this.status = '';
		}
		else if (this.selectedStatus === 'Active') {
			this.status = 'approved';
		}
		else if (this.selectedStatus === 'Pending') {
			this.status = 'pending';
		}
		else if (this.selectedStatus === 'Blocked') {
			this.status = 'blocked';
		}

		this.page = 1;
		this.getAllJobs();
	}


	onSearch(): void {
		this.searchSubject.next(this.searchTerm?.trim() || '');
	}


	exportToExcel(): void {

		const exportData = this.jobData.map(job => ({
			'Company': job.company_name,
			'Job Title': job.job_title,
			'Job Type': job.job_type,
			'Work Mode': job.work_mode,
			'Applied': job.applied_count,
			'Shortlisted': job.shortlisted_count,
			'Hired': job.hired_count,
			'Openings': job.number_of_openings,
			'Status': job.status,
			'Deadline': job.application_deadline,
			'Created': job.created_at
		}));

		const worksheet = XLSX.utils.json_to_sheet(exportData);
		const workbook: XLSX.WorkBook = {
			Sheets: { Jobs: worksheet },
			SheetNames: ['Jobs']
		};

		const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
		const blob = new Blob(
			[excelBuffer],
			{ type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8' });
		saveAs(blob, `jobs_${new Date().toISOString().slice(0, 10)}.xlsx`);
		this.toastr.success('Excel exported successfully');
	}


	prevPage(): void {
		if (this.page > 1) {
			this.page--;
			this.getAllJobs();
		}
	}

	nextPage(): void {
		if (this.page < this.totalPages) {
			this.page++;
			this.getAllJobs();
		}
	}

	closeMenus(): void {
		this.showActionMenuId = null;
	}


	getAllJobs(): void {
		this.isLoading = true;
		const filter: any = {};
		if (this.selectedStatus) {
			filter.job_type = this.selectedStatus;
		}
		if (this.selectedWorkMode) {
			filter.work_mode = this.selectedWorkMode;
		}
		if (this.selectedCreatedDate) {
			filter.created_at = this.selectedCreatedDate;
		}
		if (this.selectedJobRole) {
			filter.job_title = this.selectedJobRole;
		}
		if (this.selectedCompanyFilter) {
			filter.company_name = this.selectedCompanyFilter;
		}
		const filterPayload = Object.keys(filter).length > 0 ? filter : undefined;

		this.subscription.add(
			this.jobService.getAllJobs(
				this.page,
				this.limit,
				'active',
				this.searchTerm?.trim() || undefined,
				filterPayload
			).pipe(
				finalize(() => {
					this.isLoading = false;
				})
			).subscribe({
				next: (response) => {
					if (response.Success) {
						this.total = response.Data.total;
						this.page = response.Data.page;
						this.limit = response.Data.limit;
						this.totalPages = response.Data.total_pages;
						this.jobData = response?.Data.jobs || [];
						this.showingFrom = this.total === 0 ? 0 : ((this.page - 1) * this.limit) + 1;
						this.showingTo = Math.min(this.page * this.limit, this.total);
					} else {
						this.toastr.error(response.Message);
					}
				},
				error: () => {
					this.toastr.error('Failed to fetch jobs');
				}
			})
		);
	}


	onDateChange(): void {
		this.page = 1;
		this.getAllJobs();
	}

	clearCreatedDateFilter(): void {
		this.selectedCreatedDate = '';
		this.page = 1;
		this.getAllJobs();
	}

	clearDeadlineDateFilter(): void {
		this.selectedDeadlineDate = '';
		this.page = 1;
		this.getAllJobs();
	}

	resetFilters(): void {
		this.searchTerm = '';
		this.selectedStatus = '';
		this.selectedWorkMode = '';
		this.selectedCreatedDate = '';
		this.selectedDeadlineDate = '';
		this.selectedJobRole = '';
		this.selectedCompanyFilter = '';
		this.page = 1;
		this.getAllJobs();
	}

	onJobRoleChange(): void {
		this.page = 1;
		this.getAllJobs();
	}

	onCompanyFilterChange(): void {
		this.page = 1;
		this.getAllJobs();
	}

	onWorkModeChange(): void {
		this.page = 1;
		this.getAllJobs();
	}


	openOnboardModal(): void {
		this.showOnboardModal = true;
	}


	toggleActionMenu(event: Event, companyId: string): void {
		event.stopPropagation();

		this.showActionMenuId =
			this.showActionMenuId === companyId ? null : companyId;
	}


	sortBy(field: string): void {

		if (this.sortField === field) {
			this.sortDirection =
				this.sortDirection === 'asc' ? 'desc' : 'asc';
		}
		else {
			this.sortField = field;
			this.sortDirection = 'asc';
		}

		this.jobData.sort((a: any, b: any) => {

			let valueA = a[field];
			let valueB = b[field];

			if (valueA == null) valueA = '';
			if (valueB == null) valueB = '';

			if (typeof valueA === 'string') {
				valueA = valueA.toLowerCase();
				valueB = valueB.toLowerCase();
			}

			if (valueA > valueB) {
				return this.sortDirection === 'asc' ? 1 : -1;
			}

			if (valueA < valueB) {
				return this.sortDirection === 'asc' ? -1 : 1;
			}

			return 0;
		});
	}


	//goToCompanyApprovalDetailPage(jobId: string) {
	//	this.router.navigate(['admin-student-platform/company-management/approval-detail-page', jobId]);
	//}
	

	goToJobDetailPage(jobId: string) {
		this.router.navigate(['admin-student-platform/company-management/job-approval-detail-page', jobId], { queryParams: { origin: 'all-jobs', page: this.page } })
	}

	truncateText(text: string, limit: number): string {
		return text?.length > limit ? text.slice(0, limit) + '...' : text;
	}


	ngOnDestroy(): void {
		this.subscription.unsubscribe();
		this.searchSubscription.unsubscribe();
	}

}

